import type { Strategy, UserConfig } from "@investhinks/api-client";
import type { TFunction } from "i18next";
import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

import { getStrategyName } from "../../../../../utils/risks";

interface ConfigSidebarProps {
  strategy?: Strategy | null;
  userConfig?: UserConfig;
  stopLoss?: number;
  symbol?: string;
  onEditBot?: () => void;
  canEdit?: boolean;
  t: TFunction;
}

const ConfigSidebar: React.FC<ConfigSidebarProps> = ({
  strategy,
  userConfig,
  stopLoss,
  symbol,
  onEditBot,
  canEdit = false,
  t,
}) => {
  const { sailingModeThreshold, maxActiveBalance, maxLeverage, risk } = userConfig || {};

  return (
    <Card className="mb-2 card-hover">
      <CardHeader className="py-2 bg-transparent d-flex justify-content-between align-items-center">
        <h6 className="mb-0 text-uppercase text-muted" style={{ fontSize: "0.7rem" }}>
          <i className="fas fa-sliders-h mr-2" />
          {t("configSummary.title")}
        </h6>
        {canEdit && (
          <i className="fas fa-cog text-muted cursor-pointer" style={{ fontSize: "0.85rem" }} onClick={onEditBot} />
        )}
      </CardHeader>
      <CardBody className="pt-0 pb-2">
        <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
          <small className="text-muted">{t("configSummary.strategy")}</small>
          <span className="font-weight-bold">{getStrategyName(strategy, risk)}</span>
        </div>
        <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
          <small className="text-muted">{t("configSummary.stopLoss")}</small>
          <span className="font-weight-bold">
            {stopLoss ? `${(stopLoss * 100).toFixed(0)}%` : t("configSummary.off")}
          </span>
        </div>
        <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
          <small className="text-muted">{t("configSummary.leverage")}</small>
          <span className="font-weight-bold">1:{maxLeverage || "1000"}</span>
        </div>
        {!!strategy?.configOptions?.maxActiveBalance && (
          <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
            <small className="text-muted">{t("configSummary.mab")}</small>
            <span className="font-weight-bold">
              {maxActiveBalance ? `${maxActiveBalance.toFixed(0)} ${symbol}` : t("configSummary.off")}
            </span>
          </div>
        )}
        {!!strategy?.configOptions?.sailingMode && (
          <div className="d-flex justify-content-between align-items-center py-1">
            <small className="text-muted">{t("configSummary.sailingMode")}</small>
            <span className={`font-weight-bold ${sailingModeThreshold ? "text-success" : ""}`}>
              {sailingModeThreshold ? `${sailingModeThreshold}%` : t("configSummary.off")}
            </span>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ConfigSidebar;
